import { Card } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../server";
import { RiAuctionLine, RiTimeLine, RiCoinLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

export default function CurrentAuction() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [auctions, setAuctions] = useState([]);

  // Separate state for each parameter
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [liveAuction, setLiveAuction] = useState(true);
  const [userWise, setUserWise] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);

  const getAllAuctions = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await axios.get(`${server}/shop/all`, {
        params: {
          pageNo: pageNo,
          pageSize: pageSize,
          sortBy: sortBy,
          sortOrder: sortOrder,
          liveAuction: liveAuction,
          userWise: userWise,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
      });
      setAuctions(data?.data || []);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      // Only show error on non-silent load or if it's a critical failure
      if (!silent) {
        toast.error(e?.response?.data?.message || e?.message || "Failed to fetch auctions");
      }
    }
  };

  useEffect(() => {
    getAllAuctions();

    const refreshInterval = setInterval(() => {
      getAllAuctions(true);
      setTimeLeft(10); // Reset countdown
    }, 10000);

    const countdownInterval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 10));
    }, 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [pageNo, pageSize, sortBy, sortOrder, liveAuction, userWise]);

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    // Convert "16:56:00" to "4:56 PM"
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getAuctionStatus = (auction) => {
    if (!auction.bidDate || !auction.openTime || !auction.closeTime) {
      return { status: "Not Scheduled", color: "bg-gray-100 text-gray-800 border-gray-200" };
    }

    const now = new Date();
    const auctionDate = new Date(auction.bidDate);
    const [openHours, openMinutes, openSeconds] = auction.openTime.split(":").map(Number);
    const [closeHours, closeMinutes, closeSeconds] = auction.closeTime.split(":").map(Number);

    const openDateTime = new Date(auctionDate);
    openDateTime.setHours(openHours, openMinutes, openSeconds);

    const closeDateTime = new Date(auctionDate);
    closeDateTime.setHours(closeHours, closeMinutes, closeSeconds);

    if (now < openDateTime) {
      return { status: "Upcoming", color: "bg-blue-100 text-blue-800 border-blue-200" };
    } else if (now >= openDateTime && now <= closeDateTime) {
      return { status: "Live", color: "bg-green-100 text-green-800 border-green-200" };
    } else {
      return { status: "Closed", color: "bg-red-100 text-red-800 border-red-200" };
    }
  };

  return (
    <div className="flex justify-center w-full p-4 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="w-full max-w-7xl">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4 justify-center">
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-2xl shadow-lg">
              <RiAuctionLine className="text-5xl text-white" />
            </div>
            <div className="text-center">
              <h1 className="text-5xl font-bold text-slate-800 tracking-tight">
                Live Auction
              </h1>
              <p className="text-slate-500 mt-2 text-lg">
                Active auctions happening now
              </p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1 bg-white/50 border border-slate-200 rounded-full shadow-sm">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Refreshing in {timeLeft}s
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Auctions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full flex flex-col items-center justify-center py-20">
              <svg className="animate-spin h-12 w-12 text-green-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-slate-500 font-medium">Loading auctions...</p>
            </div>
          ) : auctions.length === 0 ? (
            <div className="col-span-full flex flex-col items-center justify-center py-20">
              <div className="bg-slate-100 p-6 rounded-full mb-4">
                <RiAuctionLine className="text-4xl text-slate-400" />
              </div>
              <p className="text-slate-800 font-medium text-lg">No live auctions found</p>
              <p className="text-slate-500 text-sm mt-1">
                No auctions are currently active
              </p>
            </div>
          ) : (
            auctions.map((auction) => {
              const statusInfo = getAuctionStatus(auction);
              return (
                <Card
                  key={auction.id || auction.guid}
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                    },
                  }}
                  className="bg-white cursor-pointer"
                  onClick={() => navigate("/my/bidDetails", { state: { shopId: auction.id } })}
                >
                  <div className="p-6">
                    {/* Status Badge */}
                    <div className="flex justify-between items-start mb-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${statusInfo.color}`}>
                        {statusInfo.status}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatDate(auction.bidDate)}
                      </span>
                    </div>

                    {/* Shop Info */}
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {auction.shopName}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Code: <span className="font-medium text-slate-700">{auction.shopCode}</span>
                    </p>

                    {/* Time Info */}
                    <div className="flex items-center gap-4 mb-4 pb-4 border-b border-slate-100">
                      <div className="flex items-center gap-2 text-sm">
                        <RiTimeLine className="text-green-500" />
                        <span className="text-slate-600">
                          {formatTime(auction.openTime)} - {formatTime(auction.closeTime)}
                        </span>
                      </div>
                    </div>

                    {/* Price Info */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Base Price:</span>
                        <span className="text-lg font-bold text-slate-800 flex items-center">
                          <RiCoinLine className="text-green-500 mr-1" />
                          {auction.basePrice?.toLocaleString("en-IN") || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Highest Bid:</span>
                        <span className="text-lg font-bold text-green-600 flex items-center">
                          <RiCoinLine className="text-green-500 mr-1" />
                          {auction.heighestAuctionBidAmount?.toLocaleString("en-IN") || "No bids yet"}
                        </span>
                      </div>
                    </div>


                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {!loading && auctions.length > 0 && (
          <div className="mt-8 flex justify-center items-center gap-4">
            <button
              onClick={() => setPageNo((prev) => Math.max(1, prev - 1))}
              disabled={pageNo === 1}
              className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Previous
            </button>
            <span className="text-slate-600 font-medium">
              Page {pageNo}
            </span>
            <button
              onClick={() => setPageNo((prev) => prev + 1)}
              disabled={auctions.length < pageSize}
              className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}